import { Injectable } from '@angular/core';

import { AccaBoostConfig, BetslipBarConfig, BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { Decimal } from 'decimal.js';
import { BetBuilderConfigService } from 'packages/sports/web/app/src/betbuilder/services/betbuilder-config.service';

import { CurrencyPipe } from '../../../base/pipes/currency.pipe';
import { BetslipType } from '../../../core/betslip-type';
import { IBetslipSummary } from '../../summary/models';
import { getPossibleWinningsNet } from '../../summary/services/betslip-summary-builder.service';
import BetslipTaxationService from '../../taxation/services/betslip-taxation.service';
import { BetslipBarTranslations } from './betslip-bar.service';

@Injectable()
export class BetslipBarReturnsMessageService {
    private readonly translations: BetslipBarTranslations;

    constructor(
        private interpolationPipe: StringInterpolationPipe,
        private currencyPipe: CurrencyPipe,
        private betslipBarConfig: BetslipBarConfig,
        private accaBoostConfig: AccaBoostConfig,
        { betslip: { betslipBar } }: Sitecore,
        private betslipConfig: BetslipConfig,
        private betBuilderConfigService: BetBuilderConfigService,
        private taxationService: BetslipTaxationService,
    ) {
        this.translations = betslipBar;
    }

    getReturnsMessage(summary: IBetslipSummary, pickCount: number, hasAccaBoost: boolean, currentType: BetslipType | null): string | undefined {
        if (!summary.possibleWinnings) {
            return;
        }

        if (summary.missingBetBuilderLegs && this.betBuilderConfigService.showEmbeddedUi()) {
            return this.translations.BetBuilderMissingPicks;
        }

        const possibleWinnings = this.calculatePossibleWinnings(summary);

        const isSingleBetBuilder =
            summary.hasBetBuilder &&
            this.betslipConfig.allowBetbuilderCombos &&
            summary.slipSummaries.length === 1 &&
            !summary.slipSummaries.find((s) => Object.keys(s.slip.slipPicks).length > 1);

        const message = hasAccaBoost ? this.translations.AccaBoostReturnsMessage : this.messageByBetslipType(currentType, isSingleBetBuilder);

        if (pickCount > 1) {
            return this.interpolationPipe.transform(message, {
                stake: this.formatCurrency(summary.totalStake),
                pickCount,
                wins: this.formatCurrency(possibleWinnings),
                winsClass: this.getWinningsHtmlClass(hasAccaBoost),
                picks: this.getReturnsText(pickCount),
                originalWinnings: this.getOriginalWinnings(summary),
            });
        }

        return this.interpolationPipe.transform(message, {
            stake: this.formatCurrency(summary.totalStake),
            wins: this.formatCurrency(possibleWinnings),
            winsClass: this.getWinningsHtmlClass(),
        });
    }

    private getOriginalWinnings(summary: IBetslipSummary): string | undefined {
        const accaBoostModifications = summary.slipSummaries?.length === 1 && summary.slipSummaries[0].rewardTokenModifications?.accaBoost;
        if (accaBoostModifications) {
            const originalWinnings = accaBoostModifications.originalWinnings;
            const originalWinningsNet =
                getPossibleWinningsNet(
                    accaBoostModifications.originalWinnings,
                    this.taxationService.getTaxationForAmount(accaBoostModifications.originalWinnings),
                ) || null;

            return this.currencyPipe.transform(originalWinningsNet || originalWinnings || null, true);
        }

        return '';
    }

    private getReturnsText(pickCount: number): string {
        switch (pickCount) {
            case 2:
                return this.translations.Double;
            case 3:
                return this.translations.Treble;
            default:
                return pickCount + ' ' + this.translations.Fold;
        }
    }

    private getWinningsHtmlClass(hasAccaBoost = false): string {
        const className = 'betslip-bar__wins';
        if (hasAccaBoost) {
            return `${className} betslip-bar__wins--acca-boost ${this.accaBoostConfig.icon}`;
        }

        return className;
    }

    private calculatePossibleWinnings(summary: IBetslipSummary): Decimal | null {
        const stake = summary.totalStake ?? new Decimal(0);
        const taxationAmount = summary.taxation?.amount ?? new Decimal(0);

        const possibleWinnings = this.tryGetBoostedWinnings(summary) ?? summary.possibleWinnings;
        const taxationAdjustedWinnings = possibleWinnings?.minus(taxationAmount) ?? null;

        if (this.betslipBarConfig.includeStakeInWinnings) {
            return taxationAdjustedWinnings;
        }

        return taxationAdjustedWinnings?.minus(stake) ?? null;
    }

    private tryGetBoostedWinnings({ slipSummaries = [] }: IBetslipSummary): Decimal | null {
        return slipSummaries[0]?.rewardTokenModifications?.oddsBoost?.boostedWinnings ?? null;
    }

    private formatCurrency(value: Decimal | null): string {
        return this.currencyPipe.transform(value, true, 2);
    }

    private messageByBetslipType(currentType: BetslipType | null, isSingleBetBuilder: boolean): string {
        switch (currentType) {
            case BetslipType.Single: {
                return isSingleBetBuilder ? this.translations.SingleReturnsBetBuilder : this.translations.SingleReturns;
            }
            case BetslipType.Combo: {
                return isSingleBetBuilder ? this.translations.MultiReturnsBetBuilder : this.translations.MultiReturns;
            }
            case BetslipType.BetBuilder: {
                return this.translations.SingleReturnsBetBuilder;
            }
            case BetslipType.System: {
                return this.translations.SystemReturns;
            }
            case BetslipType.Teaser:
            case BetslipType.EditBet:
            default: {
                return this.translations.StandardReturns;
            }
        }
    }
}
